import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { api, formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Users, UserPlus, Loader2, Trash2, MailX, Clock, Crown, LogOut } from "lucide-react";
import TeamAlert from "@/components/TeamAlert";
import { toast } from "sonner";

const BAND_TONE = { Outstanding: "text-accent", Good: "text-emerald-600", "Requires Improvement": "text-amber-500", Inadequate: "text-destructive" };

export default function Team() {
  const { refresh } = useAuth();
  const [team, setTeam] = useState(null);
  const [email, setEmail] = useState("");
  const [busy, setBusy] = useState(false);

  const load = () => api.get("/team").then((r) => setTeam(r.data));
  useEffect(() => { load(); }, []);

  const invite = async (e) => {
    e.preventDefault();
    if (!email.trim()) return;
    setBusy(true);
    try {
      const { data } = await api.post("/team/invites", { email: email.trim() });
      setTeam(data); setEmail("");
      toast.success(data.email_sent ? "Invitation emailed" : "Invite created — the email could not be delivered to that address");
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  const revoke = async (id) => { const { data } = await api.delete(`/team/invites/${id}`); setTeam(data); toast.success("Invitation withdrawn"); };
  const remove = async (id) => { const { data } = await api.delete(`/team/members/${id}`); setTeam(data); toast.success("Member removed"); };
  const leave = async () => { await api.post("/team/leave"); await refresh(); load(); toast.success("You've left the team"); };

  if (!team) return <div className="flex justify-center py-32"><Loader2 className="w-8 h-8 animate-spin text-primary" /></div>;

  return (
    <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-10" data-testid="team-page">
      <div className="mb-8">
        <span className="overline text-accent">Team</span>
        <h1 className="font-display text-3xl font-bold tracking-tight mt-1">Manager seats</h1>
        <p className="text-muted-foreground mt-1">Enterprise includes up to {team.seats_total} managers sharing one subscription and policy library.</p>
      </div>

      {team.role === "member" && (
        <div className="bg-card border border-border rounded-2xl p-6 shadow-sm" data-testid="team-member-view">
          <div className="flex items-center gap-3 mb-3"><Crown className="w-5 h-5 text-amber-500" /><p className="font-display font-semibold">You're part of {team.owner_name}'s team</p></div>
          <p className="text-sm text-muted-foreground mb-4">You share their Enterprise subscription — unlimited AI assistant, mock interviews and the shared library.</p>
          <Button variant="outline" size="sm" onClick={leave} data-testid="team-leave-button"><LogOut className="w-4 h-4 mr-1.5" /> Leave team</Button>
        </div>
      )}

      {team.role === "none" && (
        <div className="bg-card border border-border rounded-2xl p-8 shadow-sm text-center" data-testid="team-upsell">
          <Users className="w-10 h-10 mx-auto text-primary mb-3" />
          <p className="font-display font-semibold text-lg mb-1">Team seats come with Enterprise</p>
          <p className="text-sm text-muted-foreground mb-5">Upgrade to invite up to {team.seats_total - 1} colleagues to share your subscription and library.</p>
          <Button asChild><Link to="/pricing">See Enterprise plan</Link></Button>
        </div>
      )}

      {team.role === "owner" && (
        <div className="space-y-6">
          <div className="bg-card border border-border rounded-2xl p-6 shadow-sm">
            <div className="flex items-center justify-between gap-3 mb-4 flex-wrap">
              <h2 className="font-display font-bold text-lg flex items-center gap-2"><UserPlus className="w-5 h-5 text-primary" /> Invite a manager</h2>
              <Badge variant="secondary" data-testid="team-seats-badge">{team.seats_used}/{team.seats_total} seats used</Badge>
            </div>
            <form onSubmit={invite} className="flex gap-2 flex-wrap">
              <Input type="email" placeholder="colleague@yourservice.co.uk" value={email} onChange={(e) => setEmail(e.target.value)} className="flex-1 min-w-[240px]" data-testid="team-invite-email-input" />
              <Button type="submit" disabled={busy || team.seats_used >= team.seats_total} data-testid="team-invite-submit">
                {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <><UserPlus className="w-4 h-4 mr-1.5" /> Send invite</>}
              </Button>
            </form>
          </div>

          <div className="bg-card border border-border rounded-2xl p-6 shadow-sm">
            <h2 className="font-display font-bold text-lg mb-4 flex items-center gap-2"><Users className="w-5 h-5 text-primary" /> Members</h2>
            <ul className="divide-y divide-border/60">
              <li className="flex items-center justify-between py-3 text-sm"><span className="flex items-center gap-2"><Crown className="w-4 h-4 text-amber-500" /> You (owner)</span><Badge variant="outline">Owner</Badge></li>
              {team.members.map((m) => (
                <li key={m.id} className="py-3 text-sm" data-testid={`team-member-${m.id}`}>
                  <div className="flex items-center justify-between gap-3">
                    <span className="min-w-0"><span className="block truncate font-medium">{m.name || m.email}</span><span className="text-xs text-muted-foreground">{m.email}</span></span>
                    <div className="flex items-center gap-3 shrink-0">
                      <span className="text-right">
                        <span className={`block font-display font-bold text-lg leading-none ${BAND_TONE[m.readiness_band] || ""}`} data-testid={`team-member-score-${m.id}`}>{m.readiness_score}</span>
                        <span className="text-[10px] text-muted-foreground">{m.readiness_band}</span>
                      </span>
                      <Button size="sm" variant="ghost" className="text-destructive hover:text-destructive" onClick={() => remove(m.id)} data-testid={`team-remove-${m.id}`}><Trash2 className="w-4 h-4" /></Button>
                    </div>
                  </div>
                  <div className="flex flex-wrap gap-x-4 gap-y-1 mt-1.5 text-[11px] text-muted-foreground" data-testid={`team-member-insights-${m.id}`}>
                    <span>{m.regulator} · checklist {m.checklist}</span>
                    <span>{m.interviews_completed} mock interview{m.interviews_completed === 1 ? "" : "s"}</span>
                    <span>{m.days_left != null ? `${m.days_left} days to inspection` : "no inspection date"}</span>
                    <span>Last active {m.last_active ? new Date(m.last_active).toLocaleDateString("en-GB", { day: "numeric", month: "short" }) : "—"}</span>
                  </div>
                </li>
              ))}
              {team.invites.map((i) => (
                <li key={i.id} className="flex items-center justify-between gap-3 py-3 text-sm" data-testid={`team-invite-${i.id}`}>
                  <span className="min-w-0 flex items-center gap-2"><Clock className="w-4 h-4 text-muted-foreground" /><span className="truncate">{i.email}</span>
                    <Badge variant="secondary" className="text-[10px]">Pending</Badge>
                    {i.email_sent === false && <span className="text-[11px] text-amber-600 flex items-center gap-1"><MailX className="w-3 h-3" /> email undelivered</span>}
                  </span>
                  <Button size="sm" variant="ghost" onClick={() => revoke(i.id)} data-testid={`team-revoke-${i.id}`}>Withdraw</Button>
                </li>
              ))}
              {!team.members.length && !team.invites.length && <li className="py-6 text-sm text-muted-foreground text-center">No members yet — send your first invite above.</li>}
            </ul>
          </div>
          <TeamAlert />
        </div>
      )}
    </div>
  );
}
